import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid2";
import { Box, Button, Divider, InputAdornment, SelectChangeEvent, Tab, Tabs, TextField, Typography, IconButton, Tooltip, Popover, FormControlLabel, Switch } from "@mui/material";
import Dropdown from "../../components/dropdowns/Dropdown";
import { useTheme } from "@mui/material/styles";
import EditableTable from "../../components/EditableTable/EditableTable";
import RateCardTable from "../../components/EditableTable/RateCardTable";
import searchIcon from "../../assets/images/search.svg"
import { getContractAttributes, getContractList, getUpdateAttributes, getExportData, getBulkExportData } from "../../api";
import { showLoader, hideLoader } from "../../store/loaderSlice";
import { useDispatch,useSelector } from "react-redux";
import ReusableModal from "../../components/ReusableModal/ReusableModal";
import Success from "../../assets/images/CheckMarkGreen.svg";
import { RootState } from "../../store/appStore";
import SearchableDropdown from "../../components/SearchableDropdown/SeachableDropdown";
import { showSnackbar } from "../../store/snackBarSlice";
import SettingsIcon from "@mui/icons-material/Settings";
import JSONTable from "../../components/JSONTable/JSONTable";
import PriceReferences from "../../components/PriceReferences/PriceReferences";

// Columns for the tables
interface Contract {
  contract_workspace: string;
  contract_id: number;
  status: string;
  comments: string;
  created_at: string;
  updated_at: string;
  file_count: number;
}
interface AutoPopulatorRow {
  answer_id: number;
  [key: string]: any;  // Allow other fields dynamically
}

interface ComplianceCheckerRow {
  answer_id: number;
  [key: string]: any;  // Allow other fields dynamically
}

interface RateCardRow {
  answer_id: number;
  [key: string]: any;  // Allow other fields dynamically
}

interface PriceAttribute {
  answer_id: number;
  attribute: string;
  ai_value: string;
  [key: string]: any;
}

const TabPanel = (props: { children?: React.ReactNode; index: number; value: number }) => {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ background: "#fff", width: "100%" }}>{children}</Box>}
    </div>
  );
};

const Attributes: React.FC = () => {
  const [selectedWorkspace, setSelectedWorkspace] = useState("");
  const [workspaces, setWorkspaces] = useState([]);
  const [value, setValue] = useState(0);
  const [search, setSearch] = useState("");
  const theme = useTheme();
  const dispatch = useDispatch();
  const [isEditable, setIsEditable] = useState(false);
  const [rowsAutoPopulator, setRowsAutoPopulator] = useState<AutoPopulatorRow[]>([]);
  const [rowsComplianceChecker, setRowsComplianceChecker] = useState<ComplianceCheckerRow[]>([]);
  const [rowsRateCard, setRowsRateCard] = useState<RateCardRow[]>([]);
  const [rowsRateCardattrb, setRowsRateCardattrb] = useState<RateCardRow[]>([]);
  const [rowsPriceAttributes, setRowsPriceAttributes] = useState<PriceAttribute[]>([]);
  const [filteredPriceData, setFilteredPriceData] = useState<PriceAttribute[]>([]);
  const [originalRowsAutoPopulator, setOriginalRowsAutoPopulator] = useState([]);
  const [originalRowsComplianceChecker, setOriginalRowsComplianceChecker] = useState([]);
  const [originalRowsRateCard, setOriginalRowsRateCard] = useState([]);
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const workspaceFromStore = useSelector(
		(state: RootState) => state.common.workspacePayload,
	);

  // Column visibility settings
  const [columnSettings, setColumnSettings] = useState({
    aiReasoning: false,        // Hidden by default
    references: false,         // Hidden by default
    userAddedValue: false,     // Hidden by default
    userAddedReasoning: false, // Hidden by default
  });
  const [settingsAnchorEl, setSettingsAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpen = (key:string) => {setOpen(true)
    setKey(key);
  };
	const handleClose = () => setOpen(false)

  const handleSaveConfirmed = async () => {
    handleClose()
   await handleSave(key)
   
  }

  const fetchWorkspaces = async () => {
    dispatch(showLoader());
    try {
     const contractsData = await getContractList("", 'name');
     if(contractsData && contractsData.success){
        const contractsCWData = contractsData?.data?.contracts?.filter((ele:any) => ele.status === 'ANALYSED')?.map((contract:any) => ({
        id: contract.contract_id.toString() || "",
        label: contract.contract_workspace || "Unnamed Workspace",  
      })) || [];
      setWorkspaces(contractsCWData);
      
      if (contractsCWData.length > 0) {
        const {contract_id, contract_worspace} = workspaceFromStore
        const workspaceId = (contract_id.toString() || "") || contractsCWData[0].id || "";
        setSelectedWorkspace(workspaceId);
        
        // Immediately fetch price attributes to build dynamic tabs
        if (workspaceId) {
          console.log("Eager loading price attributes for workspace:", workspaceId);
          fetchContractAttributes(Number(workspaceId), "price", "");
        }
      }
     }
     else{
      dispatch(
        showSnackbar({
          message:  `${contractsData.message}`,
          severity: "error",
        }),
      );
     }
 
    } catch (err) {
      dispatch(
        showSnackbar({
          message:  `${err}`,
          severity: "error",
        }),
      );
    } finally {
      dispatch(hideLoader());
    }
  };

  const fetchContractAttributes = async (cwID:number, queVal:string,search:string, attributeFilter?:string) => {
    try {
      dispatch(showLoader());
      const fetchedAttributes: any = await getContractAttributes(Number(cwID),queVal,search,attributeFilter);
     
      if ( fetchedAttributes && fetchedAttributes?.data && Array.isArray(fetchedAttributes?.data) && fetchedAttributes.success) {
        if (queVal === "entity_extraction") {
          setRowsAutoPopulator(fetchedAttributes?.data);  // Set the rows for AutoPopulator
          setOriginalRowsAutoPopulator(JSON.parse(JSON.stringify(fetchedAttributes?.data)));  // Deep copy    
        } else if (queVal === "compliance_checker") {
          setRowsComplianceChecker(fetchedAttributes?.data);  // Set the rows for ComplianceChecker
          setOriginalRowsComplianceChecker(JSON.parse(JSON.stringify(fetchedAttributes?.data)));  // Deep copy
        } else if (queVal === "rate_card_attrb") {
          setRowsRateCard(fetchedAttributes?.data);  // Set the rows for RateCard
          setOriginalRowsRateCard(JSON.parse(JSON.stringify(fetchedAttributes?.data)));  // Deep copy
        } else if (queVal === "rate_card"){
          setRowsRateCardattrb(fetchedAttributes?.data);
        } else if (queVal === "price") {
          console.log("Price attributes received:", fetchedAttributes?.data);
          console.log("Sample price attribute:", fetchedAttributes?.data?.[0]);
          
          // If attributeFilter is provided, this is for filtered data for a specific tab
          if (attributeFilter) {
            setFilteredPriceData(fetchedAttributes?.data);
          } else {
            // Unfiltered data is used for building dynamic tabs
            setRowsPriceAttributes(fetchedAttributes?.data);
          }
        }
      } else {
        dispatch(
          showSnackbar({
            message:  `${fetchedAttributes.message}`,
            severity: "error",
          }),
        );
      }
    } catch (error) {
      dispatch(
        showSnackbar({
          message:  `${error}`,
          severity: "error",
        }),
      );
    } finally {
      dispatch(hideLoader());
    }
  };
  const UpdateAttributes = async (cwID:number, payload:any,queVal:string) => {
    try {
      dispatch(showLoader());
      const response: any = await getUpdateAttributes(Number(cwID),payload,queVal);
     if(response && response?.success){
      await  fetchContractAttributes(cwID,queVal,search);
      dispatch(
        showSnackbar({
          message:  `${response.message}`,
          severity: "success",
        }),
      );
     }else{
      dispatch(
        showSnackbar({
          message:  `${response.message}`,
          severity: "error",
        }),
      );
     }
     
    } catch (error) {
      dispatch(
        showSnackbar({
          message:  `${error}`,
          severity: "error",
        }),
      );
    } finally {
      await  fetchContractAttributes(cwID,queVal,search);
      dispatch(hideLoader());
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  useEffect(() => {
    if(selectedWorkspace){
       if (value === 0)  {fetchContractAttributes(Number(selectedWorkspace),"entity_extraction",search)}
    //else if (value === 1) {fetchContractAttributes(Number(selectedWorkspace),"compliance_checker",search)}
    /*else if (value === 2) {fetchContractAttributes(Number(selectedWorkspace),"rate_card_attrb",search) 
                           fetchContractAttributes(Number(selectedWorkspace),"rate_card",search) 
                             }*/
    else if (value >= 1) {
      const priceTabIndex = value - 1;
      const selectedPriceAttribute = rowsPriceAttributes[priceTabIndex];
      if (selectedPriceAttribute) {
        fetchContractAttributes(Number(selectedWorkspace),"price",search, selectedPriceAttribute.attribute);
      }
    }
    }
  }, [selectedWorkspace,value,search]);

  // Always fetch price attributes when workspace changes to build dynamic tabs
  useEffect(() => {
    if(selectedWorkspace){
      console.log("Fetching price attributes for workspace:", selectedWorkspace);
      fetchContractAttributes(Number(selectedWorkspace),"price","");
    }
  }, [selectedWorkspace]);


  const handleWorkspaceChange = (value:any) => {
    setSelectedWorkspace(value || "");
 
  };

  const handleChange = (event: React.SyntheticEvent,newValue: number) => {
    setSearch("")
    setIsEditable(false);
    setValue(newValue);    
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setSearch(target.value || '');
  };

  const handleUpdate = () => {
    setIsEditable(true);
    // Show User Added Value and User Added Reasoning columns when editing
    setColumnSettings(prev => ({
      ...prev,
      userAddedValue: true,
      userAddedReasoning: true
    }));
  };
  const handleCancelUpdate = (key:string) => {
    setIsEditable(false);
    // Reset User Added columns to their default hidden state when canceling edit
    setColumnSettings(prev => ({
      ...prev,
      userAddedValue: false,
      userAddedReasoning: false
    }));
    fetchContractAttributes(Number(selectedWorkspace),key,search);
  };

const handleExport =async (queVal: string) => {
  try {
    let response:any;
    if (queVal === "entity_extraction" || queVal === "compliance_checker" ){
      response = await getExportData(Number(selectedWorkspace), queVal);
    } 
   else if(queVal === "rate_card_attrb"){
    response = await getExportData(Number(selectedWorkspace), "rate_card_attrb");
      // getExportData(Number(selectedWorkspace), "rate_card");
    }
    else {
      throw new Error(`Invalid question value: ${queVal}`);
    }
    if(response && response.success){
      dispatch(
        showSnackbar({
          message:  `Attributes exported`,
          severity: "success",
        }),
      );
    }else{   
      dispatch(
        showSnackbar({
          message:  `${response.message}`,
          severity: "error",
        }),
      );
    }
  } catch (error) {
    dispatch(
      showSnackbar({
        message:  `${error}`,
        severity: "error",
      }),
    );

  }
};

const handleBulkExport = async () => {
  try {
    const response = await getBulkExportData();
    if(response && response.success){
      dispatch(
        showSnackbar({
          message:  `All attributes exported successfully`,
          severity: "success",
        }),
      );
    }else{   
      dispatch(
        showSnackbar({
          message:  `${response.message}`,
          severity: "error",
        }),
      );
    }
  } catch (error) {
    dispatch(
      showSnackbar({
        message:  `${error}`,
        severity: "error",
      }),
    );
  }
};

const handlePriceExport = (jsonData: string, attributeName: string) => {
  try {
    if (!jsonData || jsonData.trim() === '' || jsonData.trim() === '{}') {
      dispatch(
        showSnackbar({
          message: 'No data available to export',
          severity: 'warning',
        }),
      );
      return;
    }

    const parsedData = JSON.parse(jsonData);
    const entries = Object.entries(parsedData);
    
    if (entries.length === 0) {
      dispatch(
        showSnackbar({
          message: 'No data available to export',
          severity: 'warning',
        }),
      );
      return;
    }

    // Convert to CSV format using the same logic as JSONTable
    const csvHeader = 'Field,Value\n';
    const csvRows = entries.map(([key, value]) => {
      const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      const formattedValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
      // Escape quotes and wrap in quotes if contains comma
      const escapedValue = formattedValue.includes(',') || formattedValue.includes('"') 
        ? `"${formattedValue.replace(/"/g, '""')}"` 
        : formattedValue;
      return `"${formattedKey}",${escapedValue}`;
    }).join('\n');
    
    const csvContent = csvHeader + csvRows;
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${attributeName.replace(/[^a-zA-Z0-9]/g, '_')}_price_data.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    dispatch(
      showSnackbar({
        message: `${attributeName} price data exported successfully`,
        severity: 'success',
      }),
    );
  } catch (error) {
    console.error('Export error:', error);
    dispatch(
      showSnackbar({
        message: 'Failed to export price data',
        severity: 'error',
      }),
    );
  }
};
  const handleSave = async (key : string) => {
    setIsEditable(false);
    // Reset User Added columns to their default hidden state after saving
    setColumnSettings(prev => ({
      ...prev,
      userAddedValue: false,
      userAddedReasoning: false
    }));
    if(key === "Ap"){
      const updatedRowsAutoPopulator = getUpdatedRows("AutoPopulator");
      const payload = {
        form_data: updatedRowsAutoPopulator.map((item:any) => ({
          answer_id: item?.answer_id,
          question_id: item?.question_id,
          user_added_value: item?.user_added_value || "",
          user_added_reasoning: item?.user_added_reasoning || ""
        }))
      };
      if (payload.form_data.length === 0) {
        return; // Early return if the payload is empty
      }
    await  UpdateAttributes(Number(selectedWorkspace),payload,"entity_extraction")
    }
    else if(key === "Cc"){
     const updatedRowsComplianceChecker = getUpdatedRows("ComplianceChecker"); 
     const payload = {
      form_data: updatedRowsComplianceChecker.map((item:any)=> ({
        answer_id: item?.answer_id,
        question_id: item?.question_id,
        user_added_value: item?.user_added_value || "",
        user_added_reasoning: item?.user_added_reasoning || ""
      }))
    };
    if (payload.form_data.length === 0) {
      return; // Early return if the payload is empty
    }
    await UpdateAttributes(Number(selectedWorkspace),payload,"compliance_checker")
    }
    else if(key === "Rc"){
     const updatedRowsRateCard = getUpdatedRows("RateCard"); 
     const payload = {
      form_data: updatedRowsRateCard.map((item:any)=> ({
        answer_id: item?.answer_id,
        question_id: item?.question_id,
        user_added_value: item?.user_added_value || "",
        user_added_reasoning: item?.user_added_reasoning || ""
      }))
    };
    if (payload.form_data.length === 0) {
      return; // Early return if the payload is empty
    }
    await UpdateAttributes(Number(selectedWorkspace),payload,"rate_card_attrb")
    }
    
  }
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    rowId: number,
    field: string,
    tableType: string
  ) => {
    let updatedRows;
  
    if (tableType === "AutoPopulator") {
      updatedRows = rowsAutoPopulator.map((row: AutoPopulatorRow) =>
        row.answer_id === rowId ? { ...row, [field]: e.target.value } : row
      );
      setRowsAutoPopulator(updatedRows);
  
    } else if (tableType === "ComplianceChecker") {
      updatedRows = rowsComplianceChecker.map((row: ComplianceCheckerRow) =>
        row.answer_id === rowId ? { ...row, [field]: e.target.value } : row
      );
      setRowsComplianceChecker(updatedRows);
  
    } else if (tableType === "RateCard") {
      updatedRows = rowsRateCard.map((row: RateCardRow) =>
        row.answer_id === rowId ? { ...row, [field]: e.target.value } : row
      );
      setRowsRateCard(updatedRows);
    }
  };
  const getUpdatedRows = (tableType: string) => {
    let updatedRows: AutoPopulatorRow[] | ComplianceCheckerRow[] | RateCardRow[] = [];
  
    if (tableType === "AutoPopulator" && originalRowsAutoPopulator) {
      updatedRows = rowsAutoPopulator.filter((row, index) => {
        return JSON.stringify(row) !== JSON.stringify(originalRowsAutoPopulator[index]);
      });
    } else if (tableType === "ComplianceChecker" && originalRowsComplianceChecker) {
      updatedRows = rowsComplianceChecker.filter((row, index) => {
        return JSON.stringify(row) !== JSON.stringify(originalRowsComplianceChecker[index]);
      });
    } else if (tableType === "RateCard" && originalRowsRateCard) {
      updatedRows = rowsRateCard.filter((row, index) => {
        return JSON.stringify(row) !== JSON.stringify(originalRowsRateCard[index]);
      });
    }
  
    return updatedRows;
  };

  // Create dynamic tabs including price attribute tabs
  const baseTabs = ["Standard"];
  //const baseTabs = ["Standard", "Compliance Checker", "Rate Card Analytics"];

  const priceTabs = rowsPriceAttributes.map(attr => attr.attribute || `Price Tab ${attr.answer_id}`);
  const tabs = [...baseTabs, ...priceTabs];
  
  console.log("Price attributes:", rowsPriceAttributes);
  console.log("Generated tabs:", tabs);

  return (
    <Grid container sx={{ width: "100%", justifyContent: "center", mt: 8 }}>
      <Grid
        sx={{
          background: "#fff",
          borderRadius: "8px",
          width: "100%",
        }}
      >
        <Grid container sx={{ width: "100%" }} justifyContent="space-between">
          <Typography align="center" sx={{ fontSize: "1.5rem", px: 2, p: 1.5 }}>
            Contract Attributes
          </Typography>
          <Divider sx={{ width: "100%" }} />
          <Box display="flex" alignItems="center" gap="24px" sx={{ px: 2, p: 1.5 }} marginBottom={1}>
            <Typography variant="body1">Select Contract Workspace</Typography>

            {/* <Dropdown
              label=""
              value={selectedWorkspace}
              options={workspaces}
              onChange={handleWorkspaceChange}
              loading={loading}
              error={error}
              width={250}
              height={42}
            /> */}
            <SearchableDropdown
              options={workspaces}
              selectedValues={selectedWorkspace ? [selectedWorkspace] : []}
              onSelect={handleWorkspaceChange}
             />
          </Box>
        </Grid>
      </Grid>
      <Grid sx={{ width: "100%", marginTop: "24px" }}>
        <Tabs
          value={value}
          onChange={handleChange}
          sx={{
            "& .MuiTab-root": {
              display: "inline-flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "24px",
              backgroundColor: theme.palette.text.disabled,
              color: theme.palette.text.primary,
              textDecoration: "none",
              padding: "24px",
              borderRadius: "12px 12px 0px 0px",
              textAlign: "center",
              fontSize: "1rem",
              minWidth: "250px",
            },
            "& .Mui-selected": {
              backgroundColor: "#fff",
              color: theme.palette.primary.main,
              
            },
          }}
          TabIndicatorProps={{
            style: { display: "none" },
          }}
        >
          {tabs.map((tabLabel, index) => (
            <Tab key={index} label={tabLabel} />
          ))}
        </Tabs>

        <TabPanel value={value} index={0}>
          <Box display="flex" justifyContent="space-between" p={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: 550 }}>
              <TextField
                value={search}
                variant="outlined"
                size="small"
                placeholder="Search"
                fullWidth
                onChange={handleSearch}
                sx={{ maxWidth: 500 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src={searchIcon} alt="?" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Tooltip title="Column Settings">
                <IconButton 
                  onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" gap="24px">
            {!isEditable ?  (<Button disabled={rowsAutoPopulator?.length === 0} onClick={handleUpdate} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none" ,'&:disabled': {borderColor: theme.palette.action.disabled,}}}>
                Edit
              </Button>
              ):
              (<>
              <Button onClick={() => {handleOpen("Ap")}} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none" }}>
             Save 
             </Button>
             <Button onClick={() => {handleCancelUpdate("entity_extraction")}} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none" }}>
                Cancel 
                </Button>
             </> )
             }
              <Button disabled={rowsAutoPopulator?.length === 0} onClick={() =>handleExport("entity_extraction")} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", backgroundColor: theme.palette.primary.main, color: "#ffffff", textTransform: "none",border: `1px solid ${theme.palette.primary.main}`,
              '&:disabled': {borderColor: theme.palette.action.disabled,backgroundColor:"#fff" } }}>
                Export
              </Button>
              <Button onClick={handleBulkExport} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", backgroundColor: theme.palette.primary.main, color: "#ffffff", textTransform: "none",border: `1px solid ${theme.palette.primary.main}` }}>
                Bulk Export
              </Button>
            </Box>
          </Box>
          <Box sx={{padding:"16px"}}>
          <EditableTable search={search} fetchContractAttributes={fetchContractAttributes} CwId = {selectedWorkspace} tableKey={true}  data={rowsAutoPopulator}  isEditable={isEditable} tableType={"AutoPopulator"} handleInputChange={(e, rowId, field,tableType) => handleInputChange(e, rowId, field,"AutoPopulator")} columnSettings={columnSettings} />
          </Box>
          
          {/* Column Settings Popover */}
          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={() => setSettingsAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Box sx={{ p: 2, width: 250 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Column Visibility</Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={columnSettings.aiReasoning}
                    onChange={(e) => setColumnSettings(prev => ({ ...prev, aiReasoning: e.target.checked }))}
                    color="primary"
                  />
                }
                label="AI Reasoning"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={columnSettings.references}
                    onChange={(e) => setColumnSettings(prev => ({ ...prev, references: e.target.checked }))}
                    color="primary"
                  />
                }
                label="References"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={columnSettings.userAddedValue}
                    onChange={(e) => setColumnSettings(prev => ({ ...prev, userAddedValue: e.target.checked }))}
                    color="primary"
                  />
                }
                label="User Added Value"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={columnSettings.userAddedReasoning}
                    onChange={(e) => setColumnSettings(prev => ({ ...prev, userAddedReasoning: e.target.checked }))}
                    color="primary"
                  />
                }
                label="User Added Reasoning"
              />
            </Box>
          </Popover>
          
        </TabPanel>

        {/* <TabPanel value={value} index={1}>
          <Box display="flex" justifyContent="space-between" p={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: 550 }}>
              <TextField
                value={search}
                variant="outlined"
                size="small"
                placeholder="Search"
                fullWidth
                onChange={handleSearch}
                sx={{ maxWidth: 500 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src={searchIcon} alt="?" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Tooltip title="Column Settings">
                <IconButton 
                  onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" gap="24px">
            {!isEditable ?  (<Button disabled={rowsComplianceChecker?.length === 0} onClick={handleUpdate} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none",'&:disabled': {borderColor: theme.palette.action.disabled,} }}>
                Edit
              </Button>
              ):
              (<>
              <Button onClick={() => handleOpen("Cc")} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none" }}>
             Save 
             </Button>
             <Button onClick={() => {handleCancelUpdate("compliance_checker")}} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none" }}>
                Cancel 
                </Button>
             </>)
             }
              <Button disabled={rowsComplianceChecker?.length === 0} onClick={() =>handleExport("compliance_checker")} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", backgroundColor: theme.palette.primary.main, color: "#ffffff", textTransform: "none",border: `1px solid ${theme.palette.primary.main}`,
                '&:disabled': {backgroundColor:"#fff" ,borderColor: theme.palette.action.disabled,} }}>
                Export
              </Button>
              <Button onClick={handleBulkExport} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", backgroundColor: theme.palette.primary.main, color: "#ffffff", textTransform: "none",border: `1px solid ${theme.palette.primary.main}` }}>
                Bulk Export
              </Button>
            </Box>
          </Box>
          <Box sx={{padding:"16px"}}>
          <EditableTable search={search} fetchContractAttributes={fetchContractAttributes} CwId = {selectedWorkspace} tableKey={false }  data={rowsComplianceChecker} tableType={"ComplianceChecker"}  isEditable={isEditable} handleInputChange={(e, rowId, field,tableType) => handleInputChange(e, rowId, field, "ComplianceChecker")} columnSettings={columnSettings} />
          </Box>
        </TabPanel> */}

        {/* <TabPanel value={value} index={2}>
        <Box display="flex" justifyContent="space-between" p={2}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: 550 }}>
              <TextField
                value={search}
                variant="outlined"
                size="small"
                placeholder="Search"
                fullWidth
                onChange={handleSearch}
                sx={{ maxWidth: 500 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <img src={searchIcon} alt="?" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Tooltip title="Column Settings">
                <IconButton 
                  onClick={(e) => setSettingsAnchorEl(e.currentTarget)}
                  size="small"
                  sx={{ ml: 1 }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box display="flex" gap="24px">
            {!isEditable ?  (<Button disabled={rowsRateCard?.length === 0} onClick={handleUpdate} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none",'&:disabled': {borderColor: theme.palette.action.disabled, }}}>
                Edit
              </Button>
              ):
              (<>
              <Button onClick={() => handleOpen("Rc")} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none" }}>
             Save 
             </Button>
             <Button onClick={() => {handleCancelUpdate("rate_card_attrb")}} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", border: `1px solid ${theme.palette.primary.main}`, textTransform: "none" }}>
              Cancel 
             </Button>
             </>
             )
             }
              <Button disabled={rowsRateCard?.length === 0 && rowsRateCardattrb?.length === 0} onClick={() =>handleExport("rate_card_attrb")}  sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", backgroundColor: theme.palette.primary.main, color: "#ffffff", textTransform: "none",border: `1px solid ${theme.palette.primary.main}`,
                '&:disabled': {backgroundColor:"#fff" ,borderColor: theme.palette.action.disabled,} 
               }}>
                Export All
              </Button>
              <Button onClick={handleBulkExport} sx={{ fontSize: "0.875rem", width: 150, height: 38, padding: "12px 20px", backgroundColor: theme.palette.primary.main, color: "#ffffff", textTransform: "none",border: `1px solid ${theme.palette.primary.main}` }}>
                Bulk Export
              </Button>
            </Box>
          </Box>
          <Box sx={{padding:"16px"}}>
          <EditableTable search={search} fetchContractAttributes={fetchContractAttributes} CwId = {selectedWorkspace} tableKey={true}   data={rowsRateCard}  isEditable={isEditable} tableType={"RateCard"} handleInputChange={(e, rowId, field,tableType) => handleInputChange(e, rowId, field, "RateCard")} columnSettings={columnSettings} />
          <RateCardTable fetchContractAttributes={fetchContractAttributes} CwId = {selectedWorkspace} data={rowsRateCardattrb}/>
          </Box>
        </TabPanel> */}

        {/* Dynamic Price Tabs */}
        {rowsPriceAttributes.map((priceAttr, index) => {
          const tabIndex = 1 + index;
          const isCurrentTab = value === tabIndex;
          const displayData = isCurrentTab && filteredPriceData.length > 0 ? filteredPriceData[0] : priceAttr;
          
          return (
            <TabPanel key={`price-${priceAttr.answer_id}`} value={value} index={tabIndex}>
              <Box display="flex" justifyContent="space-between" p={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', maxWidth: 550 }}>
                  <Typography variant="h6" sx={{ fontSize: "1.25rem" }}>
                    {displayData.attribute || `Price Data ${displayData.answer_id}`}
                  </Typography>
                </Box>
                <Box display="flex" gap="24px">
                  <Button 
                    onClick={() => handlePriceExport(displayData.ai_value || '{}', displayData.attribute || `Price Data ${displayData.answer_id}`)}
                    disabled={!displayData.ai_value || displayData.ai_value.trim() === '' || displayData.ai_value.trim() === '{}'}
                    sx={{ 
                      fontSize: "0.875rem", 
                      width: 150, 
                      height: 38, 
                      padding: "12px 20px", 
                      backgroundColor: theme.palette.primary.main, 
                      color: "#ffffff", 
                      textTransform: "none",
                      border: `1px solid ${theme.palette.primary.main}`,
                      '&:disabled': {
                        backgroundColor: "#fff",
                        borderColor: theme.palette.action.disabled,
                        color: theme.palette.action.disabled
                      }
                    }}
                  >
                    Export
                  </Button>
                </Box>
              </Box>
              <Box sx={{ px: 2, pb: 2 }}>
                {/* References Section */}
                <PriceReferences 
                  references={displayData.context || []} 
                  title={displayData.attribute || `Price Data ${displayData.answer_id}`}
                />
                
                {/* JSON Table */}
                <JSONTable 
                  data={displayData.ai_value || '{}'} 
                  title={displayData.attribute || `Price Data ${displayData.answer_id}`}
                />
              </Box>
            </TabPanel>
          );
        })}
      </Grid>
      <ReusableModal
        open={open}
        onClose={handleClose}
        title= {
          <Box>
          <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          
          >
            <Typography>Save</Typography> 

              </Box>
              <Divider sx={{ width: "100%",marginTop: '20px'  }} />
              </Box>
        }
        content="Are you sure you want to save the changes?"
        actions={
          <>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleSaveConfirmed}>
              Save
            </Button>
          </>
        }
      />

    </Grid>
  );
};

export default Attributes;
